import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BookOpen, Plus, Trash2, FileText, Layers, Sparkles, Image as ImageIcon, Loader2, Search, Shuffle, Flame, ChevronRight, CheckCircle2, GraduationCap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import UploadDialog from "@/components/UploadDialog";
import Highlight from "@/components/Highlight";

const HERO = "https://images.unsplash.com/photo-1514369118554-e20d93546b30?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwc3R1ZHlpbmclMjBzdHVkeSUyMG5vdGVzJTIwZGVzayUyMG1pbmltYWxpc3R8ZW58MHx8fHwxNzkwNzYwNjYwfDA&ixlib=rb-4.1.0&q=85";
const PALETTE = ["#4F46E5", "#059669", "#D97706", "#E11D48", "#0891B2", "#7C3AED"];

export default function Workspace() {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [active, setActive] = useState(null); // null = all
  const [query, setQuery] = useState("");
  const [newSubject, setNewSubject] = useState("");
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [daily, setDaily] = useState(null);

  useEffect(() => { api.dailyStatus().then(setDaily).catch(() => {}); }, []);

  const load = async () => {
    setLoading(true);
    const [subs, mats] = await Promise.all([
      api.getSubjects(),
      api.getMaterials(query ? null : active, query),
    ]);
    setSubjects(subs);
    setMaterials(mats);
    setLoading(false);
  };

  useEffect(() => {
    const t = setTimeout(load, query ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [active, query]);

  const addSubject = async () => {
    if (!newSubject.trim()) return;
    setAdding(true);
    try {
      const color = PALETTE[subjects.length % PALETTE.length];
      const s = await api.createSubject({ name: newSubject.trim(), color });
      setSubjects((p) => [...p, s]);
      setNewSubject("");
      toast.success("Materia creata!");
    } catch { toast.error("Errore."); }
    finally { setAdding(false); }
  };

  const removeSubject = async (id) => {
    await api.deleteSubject(id);
    if (active === id) setActive(null);
    setSubjects((p) => p.filter((s) => s.id !== id));
    load();
    toast.success("Materia eliminata.");
  };

  const removeMaterial = async (id) => {
    await api.deleteMaterial(id);
    setMaterials((p) => p.filter((m) => m.id !== id));
    toast.success("Materiale eliminato.");
  };

  const subjectName = (id) => subjects.find((s) => s.id === id)?.name || "";
  const subjectColor = (id) => subjects.find((s) => s.id === id)?.color || "#4F46E5";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8 flex flex-col md:flex-row gap-6 lg:gap-8">
      {/* Sidebar */}
      <aside className="md:w-64 shrink-0 space-y-4">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <Layers className="h-3.5 w-3.5" /> Materie
        </div>
        <nav className="space-y-1" data-testid="subject-list">
          <SubjectItem label="Tutte le materie" active={active === null} onClick={() => setActive(null)} testid="subject-all" />
          {subjects.map((s) => (
            <div key={s.id} className="group flex items-center">
              <SubjectItem
                label={s.name}
                color={s.color}
                active={active === s.id}
                onClick={() => setActive(s.id)}
                testid={`subject-${s.id}`}
              />
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    data-testid={`delete-subject-${s.id}`}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Eliminare "{s.name}"?</AlertDialogTitle>
                    <AlertDialogDescription>Verranno eliminati anche tutti i materiali di questa materia.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annulla</AlertDialogCancel>
                    <AlertDialogAction onClick={() => removeSubject(s.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Elimina
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </nav>
        <div className="flex gap-2 pt-1">
          <Input
            data-testid="new-subject-input"
            placeholder="Nuova materia"
            value={newSubject}
            onChange={(e) => setNewSubject(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addSubject()}
            className="h-9 text-sm"
          />
          <Button data-testid="add-subject-button" size="icon" variant="secondary" className="h-9 w-9 shrink-0" onClick={addSubject} disabled={adding}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 space-y-6">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          className="relative overflow-hidden rounded-2xl border border-border bg-card"
        >
          <div className="grid sm:grid-cols-2">
            <div className="p-6 sm:p-8 flex flex-col justify-center gap-3">
              <Badge className="w-fit bg-accent text-accent-foreground border border-primary/20 gap-1.5">
                <Sparkles className="h-3 w-3" /> Assistente AI di Studio
              </Badge>
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight">
                Trasforma i tuoi appunti in riassunti, quiz e domande
              </h1>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Carica testo, PDF o foto degli appunti. L'AI genera tutto ciò che ti serve per studiare più in fretta.
              </p>
              <div className="pt-1">
                <UploadDialog subjects={subjects} defaultSubject={active} onCreated={() => load()} />
              </div>
            </div>
            <div className="hidden sm:block relative">
              <img src={HERO} alt="Studio" className="absolute inset-0 h-full w-full object-cover" />
            </div>
          </div>
        </motion.div>

        {/* Quick actions: daily challenge + exam mode */}
        <div className="grid sm:grid-cols-2 gap-4">
          <motion.button
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.05 }}
            onClick={() => navigate("/daily")}
            data-testid="daily-banner"
            className="group w-full flex items-center gap-4 rounded-2xl border border-orange-200 dark:border-orange-900/60 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/20 p-4 sm:p-5 text-left hover:shadow-md transition-all"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-orange-500 text-white shadow-sm group-hover:scale-105 transition-transform">
              <Flame className="h-5 w-5" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-display font-bold text-base">Sfida Giornaliera</h3>
                {daily?.completed && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> Fatta</span>
                )}
                {daily?.streak > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600 dark:text-orange-400"><Flame className="h-3.5 w-3.5" /> {daily.streak}</span>
                )}
              </div>
              <p className="text-sm text-muted-foreground truncate">Mini-quiz sui tuoi argomenti più deboli</p>
            </div>
            <ChevronRight className="h-5 w-5 text-orange-500 shrink-0 group-hover:translate-x-0.5 transition-transform" />
          </motion.button>

          <motion.button
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}
            onClick={() => navigate("/exam")}
            data-testid="exam-banner"
            className="group w-full flex items-center gap-4 rounded-2xl border border-border bg-card p-4 sm:p-5 text-left hover:shadow-md transition-all"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 shadow-sm group-hover:scale-105 transition-transform">
              <GraduationCap className="h-5 w-5" />
            </span>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-base">Modalità Esame</h3>
              <p className="text-sm text-muted-foreground truncate">Simulazione a tempo da più materie</p>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-transform" />
          </motion.button>
        </div>

        {/* Materials */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h2 className="font-display text-lg font-bold">
            {query ? "Risultati ricerca" : active === null ? "Tutti i materiali" : subjectName(active)}
            <span className="text-muted-foreground font-normal text-sm ml-2">({materials.length})</span>
          </h2>
          <div className="flex items-center gap-2">
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca appunti..."
                className="pl-9 h-9 w-full sm:w-56"
              />
            </div>
            {active !== null && materials.length > 0 && (
              <Button
                data-testid="mixed-review-button"
                variant="secondary"
                className="rounded-xl gap-2 h-9 shrink-0"
                onClick={() => navigate(`/mixed/${active}`)}
              >
                <Shuffle className="h-4 w-4" /> <span className="hidden sm:inline">Ripasso Misto</span>
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : materials.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-2xl">
            <BookOpen className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">Nessun materiale ancora. Carica il tuo primo materiale!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5" data-testid="materials-grid">
            {materials.map((m, i) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.04 }}
                onClick={() => navigate(`/material/${m.id}`)}
                data-testid={`material-card-${m.id}`}
                className="group cursor-pointer bg-card border border-border rounded-2xl p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full"
                    style={{ color: subjectColor(m.subject_id), backgroundColor: `${subjectColor(m.subject_id)}18` }}>
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: subjectColor(m.subject_id) }} />
                    {subjectName(m.subject_id)}
                  </span>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`delete-material-${m.id}`}
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare questo materiale?</AlertDialogTitle>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={() => removeMaterial(m.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Elimina</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                <h3 className="font-display font-bold text-base leading-snug mb-2 group-hover:text-primary transition-colors"><Highlight text={m.title} query={query} /></h3>
                <p className="text-sm text-muted-foreground line-clamp-3"><Highlight text={m.content} query={query} /></p>
                <div className="flex items-center gap-3 mt-4 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    {m.source_type === "file" ? <ImageIcon className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
                    {m.source_type === "file" ? "File" : "Testo"}
                  </span>
                  {m.quiz && <span className="inline-flex items-center gap-1 text-primary">• Quiz pronto</span>}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function SubjectItem({ label, color, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex-1 flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium text-left transition-colors ${
        active ? "bg-primary text-primary-foreground shadow-sm" : "text-foreground hover:bg-secondary"
      }`}
    >
      {color ? (
        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: active ? "#fff" : color }} />
      ) : (
        <Layers className="h-4 w-4 shrink-0" />
      )}
      <span className="truncate">{label}</span>
    </button>
  );
}
